import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'ip_cookie_consent';

// GDPR/ePrivacy consent gate for the analytics scripts (GA4 + Microsoft
// Clarity) loaded in public/index.html — Privacy Policy §10 already
// discloses those specifically and promises a consent mechanism before any
// consent-requiring tracking runs. Binary choice (Accept All / Necessary
// Only), not a full preference center — analytics is the only
// consent-requiring category in practice today, so a granular UI would be
// unused complexity. Auth (JWT cookie/localStorage) and Sentry error
// tracking are treated as necessary/security tooling and are never gated —
// same category split the Privacy Policy already documents.
export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(CONSENT_KEY)) setVisible(true);
    } catch { /* localStorage unavailable — fail closed, banner just won't show */ }
  }, []);

  const choose = (analytics) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ analytics, ts: Date.now() }));
    } catch { /* nothing to persist to — consent still applies for this page view */ }
    setVisible(false);
    // Starts tracking immediately on the current page if accepted — the
    // inline script in index.html that made this same decision already ran
    // before this banner could possibly have been answered, so without this
    // call analytics wouldn't start until the next navigation.
    if (analytics) window.__loadAnalyticsIfConsented?.();
  };

  if (!visible) return null;

  return (
    <div role="region" aria-label="Cookie consent" style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 'var(--z-sticky, 300)',
      background: '#fff', borderTop: '1px solid #E2E8F0', boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
      padding: '16px 20px',
    }}>
      <div style={{
        maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      }}>
        <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.6, flex: 1, minWidth: 240 }}>
          We use cookies for essential site functionality and, with your consent, for analytics.
          See our <Link to="/privacy" style={{ color: '#1B3A5C', fontWeight: 600 }}>Privacy Policy</Link> for details.
        </p>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          <button onClick={() => choose(false)}
            style={{ padding: '9px 16px', background: 'none', border: '1px solid #E2E8F0', color: '#374151', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Necessary Only
          </button>
          <button onClick={() => choose(true)}
            style={{ padding: '9px 16px', background: '#1B3A5C', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
